import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { tokenFromLocation } from "../lib/tokenFromLocation";

export default function ConfirmEmail() {
  const { t } = useTranslation("auth");
  // Token travels in the URL fragment (never sent to servers/analytics);
  // the path param remains as a fallback for links from older emails.
  const { token: pathToken } = useParams();
  const token = tokenFromLocation(useLocation(), pathToken);
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  // status: "loading" | "success" | "error"
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const refreshedRef = useRef(false);

  // Confirm the token once on mount. The token itself is the proof of intent,
  // so this must work whether or not the visitor currently has a session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.post("/auth/me/email/confirm", { token });
        if (!cancelled) setStatus("success");
      } catch (e) {
        if (cancelled) return;
        setError(formatApiErrorDetail(e.response?.data?.detail) || t("confirm_email.error_subtitle"));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Once confirmed, and once we know whether the visitor is signed in
  // (`user` is null while AuthContext's own session check is still in flight),
  // refresh the session once so the sidebar reflects the new email, and then
  // send the visitor to the right next step.
  useEffect(() => {
    if (status !== "success" || user === null) return;
    if (user && !refreshedRef.current) {
      refreshedRef.current = true;
      refresh();
    }
    const dest = user ? "/settings/profile" : "/login";
    const timer = setTimeout(() => navigate(dest), 2500);
    return () => clearTimeout(timer);
  }, [status, user, refresh, navigate]);

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12 bg-zinc-50" data-testid="confirm-email-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
        <Link to="/" className="inline-block mb-6 hover:opacity-80 transition-opacity" aria-label="QENVA · The Silent Guardian">
          <img
            src="/brand/v2-primary-logo.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className="h-8 w-auto block select-none"
            style={{ objectFit: "contain" }}
          />
        </Link>

        <div className="w-12 h-12 rounded-xl bg-zinc-950 grid place-items-center mb-5">
          <Mail size={20} className="text-white" />
        </div>

        {status === "loading" && (
          <div className="text-center" data-testid="confirm-email-loading">
            <Loader2 className="animate-spin mx-auto text-zinc-400" size={22} />
            <p className="text-base font-semibold text-zinc-950 mt-4">{t("confirm_email.title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{t("confirm_email.loading_subtitle")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center" data-testid="confirm-email-success">
            <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">{t("confirm_email.success_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{t("confirm_email.success_subtitle")}</p>
            {user === null ? null : user ? (
              <>
                <p className="text-sm text-zinc-500 mt-3">{t("confirm_email.success_loggedin_hint")}</p>
                <Link to="/settings/profile" className="text-sm text-zinc-950 underline mt-4 inline-block">
                  {t("confirm_email.go_to_profile")}
                </Link>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-500 mt-3">{t("confirm_email.success_loggedout_hint")}</p>
                <Link to="/login" className="text-sm text-zinc-950 underline mt-4 inline-block">
                  {t("confirm_email.go_to_login")}
                </Link>
              </>
            )}
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="confirm-email-error">
            <AlertCircle className="mx-auto text-rose-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">{t("confirm_email.error_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{error}</p>
            <Link to="/login" className="text-sm text-zinc-950 underline mt-4 inline-block">
              {t("confirm_email.back_to_login")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
